/**
 * PaystackSetupPanel — vendor connects their Paystack account (subaccount).
 * Shown when the vendor's payment_processor is set to 'paystack'. The vendor
 * picks a settlement bank, enters their account number, and we call
 * POST /vendor-admin/paystack/subaccount which either creates or updates
 * the Paystack Subaccount and stores the returned subaccount_code.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Building2, ShieldCheck, RefreshCw } from 'lucide-react';
import { vendorAdminApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

// Paystack Country slug values are lowercased and space-preserving.
const COUNTRY_OPTIONS = [
  { slug: 'nigeria', label: 'Nigeria', currency: 'NGN' },
  { slug: 'ghana', label: 'Ghana', currency: 'GHS' },
  { slug: 'south africa', label: 'South Africa', currency: 'ZAR' },
  { slug: 'kenya', label: 'Kenya', currency: 'KES' },
];

const PaystackSetupPanel = ({ vendorId, impersonating }) => {
  const vid = impersonating ? vendorId : undefined;
  const [current, setCurrent] = useState(null);
  const [country, setCountry] = useState('nigeria');
  const [banks, setBanks] = useState([]);
  const [bankQuery, setBankQuery] = useState('');
  const [banksLoading, setBanksLoading] = useState(false);
  const [banksError, setBanksError] = useState(null);
  const [form, setForm] = useState({
    business_name: '',
    settlement_bank: '',
    account_number: '',
    primary_contact_email: '',
    primary_contact_name: '',
    primary_contact_phone: '',
  });
  const [saving, setSaving] = useState(false);

  const loadCurrent = async () => {
    try {
      const r = await vendorAdminApi.getPaystackSubaccount(vid);
      setCurrent(r);
      setForm((f) => ({
        ...f,
        business_name: r.business_name || '',
        settlement_bank: r.settlement_bank || '',
        account_number: r.account_number || '',
      }));
    } catch {
      setCurrent({});
    }
  };

  const loadBanks = async (slug) => {
    setBanksLoading(true);
    setBanksError(null);
    try {
      const r = await vendorAdminApi.paystackBanks(slug);
      setBanks(r.banks || []);
    } catch (e) {
      setBanks([]);
      setBanksError(e?.response?.data?.detail || 'Could not load banks. Is Paystack configured?');
    } finally {
      setBanksLoading(false);
    }
  };

  useEffect(() => { loadCurrent(); }, [vid]);
  useEffect(() => { loadBanks(country); }, [country]);

  const filteredBanks = useMemo(() => {
    const q = bankQuery.trim().toLowerCase();
    if (!q) return banks;
    return banks.filter((b) => (b.name || '').toLowerCase().includes(q) || (b.code || '').includes(q));
  }, [banks, bankQuery]);

  const save = async () => {
    if (!form.settlement_bank || !form.account_number || !form.business_name) {
      toast.error('Business name, bank and account number are required');
      return;
    }
    setSaving(true);
    try {
      const r = await vendorAdminApi.savePaystackSubaccount({
        business_name: form.business_name,
        settlement_bank: form.settlement_bank,
        account_number: form.account_number,
        primary_contact_email: form.primary_contact_email || undefined,
        primary_contact_name: form.primary_contact_name || undefined,
        primary_contact_phone: form.primary_contact_phone || undefined,
      }, vid);
      toast.success(`Paystack subaccount ${current?.subaccount_code ? 'updated' : 'connected'}.`);
      setCurrent((c) => ({ ...(c || {}), subaccount_code: r.subaccount_code, business_name: r.business_name }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save subaccount');
    } finally {
      setSaving(false);
    }
  };

  const connected = !!current?.subaccount_code;

  return (
    <section
      className="mt-6 bg-white border border-slate-200 rounded-2xl p-5"
      data-testid="paystack-setup-panel"
    >
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-emerald-600" />
          <h2 className="font-semibold text-slate-900">Paystack payouts</h2>
          {connected && (
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium">
              <ShieldCheck className="h-3 w-3" /> Connected
            </span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => loadBanks(country)} disabled={banksLoading} data-testid="paystack-refresh-banks">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${banksLoading ? 'animate-spin' : ''}`} />
          Refresh banks
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Connect the bank account where Paystack should deposit your share of each order.
        The platform commission is applied automatically at checkout — you don&apos;t need to configure it here.
      </p>

      {connected && (
        <div className="mb-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-900" data-testid="paystack-current-subaccount">
          Subaccount code: <span className="font-mono font-semibold">{current.subaccount_code}</span>
          {current.business_name && <span> · {current.business_name}</span>}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-slate-600">Country</Label>
          <select
            value={country}
            onChange={(e) => { setCountry(e.target.value); setForm((f) => ({ ...f, settlement_bank: '' })); }}
            className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
            data-testid="paystack-country-select"
          >
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c.slug} value={c.slug}>{c.label} ({c.currency})</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Business name (as on your bank account)</Label>
          <Input
            value={form.business_name}
            onChange={(e) => setForm({ ...form, business_name: e.target.value })}
            placeholder="Your store's legal name"
            className="h-10 mt-1"
            data-testid="paystack-business-name"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs text-slate-600">Settlement bank</Label>
          <Input
            value={bankQuery}
            onChange={(e) => setBankQuery(e.target.value)}
            placeholder={banksLoading ? 'Loading banks…' : 'Search bank name'}
            className="h-10 mt-1"
            data-testid="paystack-bank-search"
          />
          {banksError && (
            <p className="text-xs text-rose-600 mt-1" data-testid="paystack-banks-error">{banksError}</p>
          )}
          <select
            value={form.settlement_bank}
            onChange={(e) => setForm({ ...form, settlement_bank: e.target.value })}
            disabled={banksLoading || !!banksError}
            size={5}
            className="mt-2 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            data-testid="paystack-bank-select"
          >
            {filteredBanks.length === 0 && !banksLoading && (
              <option value="" disabled>No banks match</option>
            )}
            {filteredBanks.map((b) => (
              <option key={b.code} value={b.code}>{b.name}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Account number</Label>
          <Input
            value={form.account_number}
            onChange={(e) => setForm({ ...form, account_number: e.target.value.replace(/[^0-9]/g, '') })}
            placeholder="0123456789"
            inputMode="numeric"
            className="h-10 mt-1 font-mono"
            data-testid="paystack-account-number"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Contact email (optional)</Label>
          <Input
            type="email"
            value={form.primary_contact_email}
            onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })}
            placeholder="finance@yourstore.com"
            className="h-10 mt-1"
            data-testid="paystack-contact-email"
          />
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <Button
          onClick={save}
          disabled={saving || banksLoading}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="paystack-save-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-1.5" />}
          {connected ? 'Update subaccount' : 'Connect Paystack'}
        </Button>
      </div>
    </section>
  );
};

export default PaystackSetupPanel;
